import { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Building2, Plus, Edit, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { useAuthStore } from '@/store/useAuthStore'

interface Client {
  id: string
  name: string
  linkedin_url?: string
  created_by?: number
  created_at?: string
}

export function ClientsManagement() {
  const { user } = useAuthStore()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    name: '',
    linkedin_url: '',
    unipile_account_id: '',
  })

  useEffect(() => {
    loadClients()
  }, [])

  const loadClients = async () => {
    try {
      setLoading(true)
      const response = await api.getClients()
      setClients(response.clients)
    } catch (error) {
      toast.error('Failed to load clients')
      console.error('Load clients error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingClient(null)
    setFormData({ name: '', linkedin_url: '', unipile_account_id: '' })
    setShowDialog(true)
  }

  const handleEdit = (client: Client) => {
    setEditingClient(client)
    setFormData({
      name: client.name,
      linkedin_url: client.linkedin_url || '',
      unipile_account_id: '',
    })
    setShowDialog(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editingClient) {
        await api.updateClient(editingClient.id, formData)
        toast.success('Client updated successfully')
      } else {
        await api.createClient(formData)
        toast.success('Client created successfully')
      }

      setShowDialog(false)
      loadClients()
    } catch (error: any) {
      toast.error(error.message || 'Operation failed')
      console.error('Client operation error:', error)
    }
  }

  const handleDelete = async (clientId: string) => {
    try {
      await api.deleteClient(clientId)
      toast.success('Client deleted successfully')
      setDeleteConfirm(null)
      loadClients()
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete client')
      console.error('Delete client error:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading clients...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Clients</h2>
          <p className="text-sm text-slate-400 mt-1">Manage client accounts for LinkedIn outreach</p>
        </div>
        {user?.role === 'admin' && (
          <Button onClick={handleCreate} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Client
          </Button>
        )}
      </div>

      <div className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-900/50 border-b border-slate-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">LinkedIn</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Created</th>
              {user?.role === 'admin' && (
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {clients.map((client) => (
              <tr key={client.id} className="hover:bg-slate-800/30">
                <td className="px-6 py-4 text-sm font-medium">{client.name}</td>
                <td className="px-6 py-4 text-sm text-slate-400">
                  {client.linkedin_url ? (
                    <a href={client.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                      Profile
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-slate-400">
                  {client.created_at ? new Date(client.created_at).toLocaleDateString() : '-'}
                </td>
                {user?.role === 'admin' && (
                  <td className="px-6 py-4 text-sm text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(client)}
                      className="gap-1"
                    >
                      <Edit className="w-3 h-3" />
                      Edit
                    </Button>
                    {deleteConfirm === client.id ? (
                      <div className="inline-flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(client.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(client.id)}
                        className="gap-1 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {editingClient ? 'Edit Client' : 'Add Client'}
              </h3>
              <button
                onClick={() => setShowDialog(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Client Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                  placeholder="e.g., John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin_url">LinkedIn Profile URL</Label>
                <Input
                  id="linkedin_url"
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>

              {user?.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="unipile_account_id">
                    Unipile Account ID {editingClient && '(leave blank to keep unchanged)'}
                  </Label>
                  <Input
                    id="unipile_account_id"
                    type="password"
                    value={formData.unipile_account_id}
                    onChange={(e) => setFormData({ ...formData, unipile_account_id: e.target.value })}
                    required={!editingClient}
                    placeholder="Enter Unipile account ID"
                  />
                  <p className="text-xs text-slate-500">
                    This ID will be encrypted and stored securely
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">
                  {editingClient ? 'Save Changes' : 'Add Client'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
